import React, { useState, useMemo } from 'react';

import { ChannelExperienceTypeEnum } from 'constants-channel';
import { useTranslation } from 'react-i18next';
import { ValidationError } from 'yup';
import {
  useUpdateUnit,
  useExternalReference,
  useUnitValidation,
  useUnitErrorHandling,
} from './hooks';
import { UnitFormFields, UnitActionButtons } from './components';

import { getAggregatedValidationMessage } from 'lane-shared/helpers';
import { ExternalReference } from 'graphql-query-contracts';

import { PageNavigationAlert } from 'components/general';
import { H4 } from 'components/typography';

import { useGetFloorFieldOptions } from '../hooks/useGetFloorsFieldOptions';
import { UnitInputFields, UnitInputFieldKey } from '../types';

import styles from './styles.scss';
import { getSharedTranslationKeys } from '../utils';
import { SourceTypeLabel } from '../../../../../../type-maps/source-type-label.map';

type Props = {
  channelId: string;
  channelExperienceType: ChannelExperienceTypeEnum | null | undefined;
  unit: {
    id: string;
    name: string;
    description?: string | null;
    floors?:
      | ({
          id: string;
          name?: string;
          index?: number;
        } | null)[]
      | null;
    externalReferences?: ExternalReference[];
  };
  setEdit: (value: boolean) => void;
  setIsUnitUpdated: (value: boolean) => void;
};

export const EditUnit = ({
  channelId,
  channelExperienceType,
  unit,
  setEdit,
  setIsUnitUpdated,
}: Props) => {
  const { t } = useTranslation();
  const { createPage, editPage, detailsPage } = getSharedTranslationKeys(
    channelExperienceType
  );

  const { enableExternalReference, externalReference } = useExternalReference(
    unit,
    channelId
  );

  const { unitValidator } = useUnitValidation(t, createPage, editPage);
  const {
    serverFormFieldErrors,
    resetServerFormFieldErrors,
    handleServerError,
  } = useUnitErrorHandling(channelExperienceType);

  const [editUnit, setEditUnit] = useState<UnitInputFields>({
    name: unit.name,
    description: unit.description ?? '',
    floors:
      unit.floors?.map(floor => ({
        label: floor?.name || floor?.index?.toString() || '',
        value: floor?.id ?? '',
      })) ?? [],
    externalIdSource: externalReference
      ? {
          label: SourceTypeLabel[externalReference?.source.sourceType],
          value: externalReference?.source.sourceType,
        }
      : null,
    externalUnitId: externalReference?.source.sourceEntityId || '',
    externalSourceEntityType: externalReference?.source.sourceEntityType || '',
  });

  const [possiblePartialUpdate, setPossiblePartialUpdate] = useState(false);
  const [updateUnitFields, setUpdateUnitFields] = useState<UnitInputFields>();
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);

  const { updateUnit, loading: isUpdatingUnit } = useUpdateUnit(
    enableExternalReference
  );

  const { options } = useGetFloorFieldOptions(channelId);

  const isPristine = useMemo(() => {
    return (
      updateUnitFields === undefined ||
      (updateUnitFields.name?.length === 0 &&
        updateUnitFields.description?.length === 0 &&
        updateUnitFields.externalUnitId?.length === 0)
    );
  }, [updateUnitFields]);

  const getErrorMessage = (field: UnitInputFieldKey) => {
    const validationMessage = getAggregatedValidationMessage(
      validationError,
      field
    );

    if (validationMessage) {
      return [validationMessage];
    }

    const serverErrorMessages = serverFormFieldErrors[field];

    return serverErrorMessages ? [serverErrorMessages] : [];
  };

  const handleOnChange = (value: any, fieldName: string) => {
    setEditUnit(prev => ({
      ...prev,
      [fieldName]: value,
    }));

    setUpdateUnitFields(
      prev =>
        ({
          ...prev,
          [fieldName]: value,
        }) as UnitInputFields
    );
  };

  const handleOnUpdate = async () => {
    try {
      resetServerFormFieldErrors();

      unitValidator.validateSync(
        {
          name: editUnit.name,
          externalIdSource: editUnit.externalIdSource?.value || '',
          externalUnitId: editUnit.externalUnitId,
        },
        { abortEarly: false }
      );

      setValidationError(null);

      await updateUnit({
        propertyId: channelId,
        unitId: unit.id,
        unit: editUnit,
        existingExternalReferences: unit.externalReferences ?? undefined,
      });

      window.Toast.show(t(editPage.successToast));

      setEdit(false);
    } catch (error) {
      if (error instanceof ValidationError) {
        setValidationError(error);
      } else {
        const isExternalReferenceError = handleServerError(error);

        if (isExternalReferenceError) {
          setPossiblePartialUpdate(true);
        }
      }
    }
  };

  const handleOnCancel = async () => {
    setEdit(false);

    if (possiblePartialUpdate) {
      setIsUnitUpdated(true);
    }
  };

  return (
    <div className={styles.unitEdit}>
      <PageNavigationAlert when={!isPristine} testId="pageNavigationAlert" />
      <div className={styles.header}>
        <H4>{t(detailsPage.header)}</H4>
      </div>
      <div className={styles.formContainer}>
        <UnitFormFields
          editUnit={editUnit}
          enableExternalReference={enableExternalReference}
          options={options}
          getErrorMessage={getErrorMessage}
          handleOnChange={handleOnChange}
          t={t}
          createPage={createPage}
          editPage={editPage}
        />
      </div>
      <div className={styles.buttonContainer}>
        <UnitActionButtons
          isPristine={isPristine}
          isUpdatingUnit={isUpdatingUnit}
          handleOnUpdate={handleOnUpdate}
          handleOnCancel={handleOnCancel}
          t={t}
        />
      </div>
    </div>
  );
};
